import React from "react";
import {
  Box,
  Grid,
  Typography,
  Avatar,
  Button,
  IconButton,
  useTheme,
  LinearProgress,
  CircularProgress
} from "@mui/material";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { useIpStats } from "../../features/location/useIpLookup";
import ExportCsvButton from "../../components/ExportCsvButton";
import { UserLocationMap } from "../../components/UserLocationMap";

const IpOverview = () => {
  const theme = useTheme();

  const { data: stats, isLoading } = useIpStats();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const { summary, countryStats, ipDots, totalCountries, totalCities } = stats || { summary: [], countryStats: [], ipDots: [], totalCountries: 0, totalCities: 0 };
  const maxIps = Math.max(...(countryStats?.map((c) => c.ips) || [0]));

  // Create custom summary cards with Countries and Cities
  const summaryCards = [
    {
      label: summary[0]?.label || "Total IPs",
      value: summary[0]?.value || 0,
      color: summary[0]?.color || "primary",
      icon: "/Images/iconoir_ip-address-tag.png"
    },
    {
      label: summary[1]?.label || "Unique IPs",
      value: summary[1]?.value || 0,
      color: summary[1]?.color || "success",
      icon: "/Images/material-symbols-light_special-character-rounded.png"
    },
    {
      label: "Total Countries",
      value: totalCountries || countryStats.length,
      color: "warning",
      icon: "/Images/typcn_location (2).png"
    },
    {
      label: "Total Cities",
      value: totalCities || 0,
      color: "info",
      icon: "/Images/mingcute_user-pin-fill (1).png"
    }
  ];

  // Icon mapping helper
  const getIcon = (iconPath) => {
    return (
      <img
        src={iconPath}
        alt="icon"
        style={{ width: 24, height: 24 }}
      />
    );
  };

  // Prepare data for UserLocationMap
  // UserLocationMap expects { lat, lng, count, name }
  const mapData = (ipDots || []).map(dot => ({
    ...dot,
    name: dot.city || dot.country || 'Location', // Fallback name
    count: dot.count || 1
  }));

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        color: "text.primary",
        minHeight: "100vh",
        p: 3,
        boxShadow: "none",
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2),
        borderRadius: 2,
      }}
    >
      {/* Summary Cards */}
      <Grid container spacing={3} mb={2}>
        {summaryCards.map((item, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Box
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                borderRadius: 2,
                boxShadow: "none",
                border: `1px solid ${theme.palette.divider}`,
                padding: theme.spacing(2),
              }}
            >
              <Box flexGrow={1}>
                <Typography color="text.secondary" variant="body2">
                  {item.label}
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {item.value}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: theme.palette[item.color].main,
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                }}
              >
                {getIcon(item.icon)}
              </Avatar>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Map and Side Panel */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mt: 2,
          boxShadow: "none",
          border: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(2),
          borderRadius: 2,
          flexDirection: { xs: 'column', md: 'row' }
        }}
      >
        {/* Map */}
        <Box
          sx={{ flex: 1, minHeight: 400, borderRadius: 2, overflow: "hidden" }}
        >
           <UserLocationMap data={mapData} />
        </Box>

        {/* Side Panel */}
        <Box
          sx={{
            width: { xs: '100%', md: 320 },
            borderRadius: 2,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 260,
            boxShadow: "none",
            border: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(2),
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {totalCountries || countryStats.length}
              </Typography>
              <Typography variant="caption">COUNTRIES</Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {totalCities || 0}
              </Typography>
              <Typography variant="caption">CITIES</Typography>
            </Box>
            <IconButton>
              <InfoIcon />
            </IconButton>
          </Box>
          <Box display="flex" gap={1}>
            <ExportCsvButton
              data={countryStats}
              filename="ip-overview.csv"
              columns={[
                { label: "Country", key: "country" },
                { label: "ISO Code", key: "isoCode" },
                { label: "IP Count", key: "ips" }
              ]}
              variant="contained"
              size="small"
              sx={{ flex: 1 }}
            />
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={<FileCopyIcon />}
              sx={{ flex: 1 }}
            >
              Copy URL
            </Button>
          </Box>
          <Box>
            {countryStats.map((c, i) => (
              <Box
                key={c.country}
                display="flex"
                flexDirection="column"
                py={1}
                borderBottom={
                  i < countryStats.length - 1
                    ? `1px solid ${theme.palette.divider}`
                    : "none"
                }
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <img
                      src={`https://flagcdn.com/w40/${c.isoCode}.png`}
                      alt={c.country}
                      style={{
                        width: 20,
                        height: 10,
                        objectFit: "cover",
                        borderRadius: 3,
                      }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <Typography>{c.country}</Typography>
                  </Box>
                  <Typography color="text.secondary">{c.ips} IPs</Typography>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={Math.round((c.ips / maxIps) * 100)}
                  sx={{
                    height: 6,
                    borderRadius: 5,
                    mt: 1,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[900]
                        : theme.palette.grey[200],
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: theme.palette.primary.main,
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default IpOverview;
